import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { UserContext } from "../../../context/UserContext";
import apiFetch, { safeJson } from "../../../context/api";
import useConfirmar from "../../Elements/useConfirmar";

/**
 * Mesas del club (CRUO F1) — sección de Ubicaciones SOLO para social_club.
 * Las mesas viven en service_tables (separadas del stock, ARQUITECTURA_CARRITO_V2
 * §2.2): aquí se crean en tanda, se dan de baja y se IMPRIMEN sus QRs.
 * El QR codifica el deep-link que fijará la mesa en la PWA del socio.
 */
export default function MesasSection() {
  // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del
  // club y la baja de la mesa moría sin decir nada (auditoría CRUO 21-09, CRUO-010).
  const { confirmar, dialogo } = useConfirmar();
  const [token, , , venueID] = useContext(UserContext);
  const [mesas, setMesas] = useState([]);
  const [cuantas, setCuantas] = useState(4);
  const [zona, setZona] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  // Edición inline de una mesa: { id, label, display_name, zone } o null.
  const [editando, setEditando] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token]
  );

  const cargar = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await apiFetch(`/v1/service-tables?venue_id=${venueID}`, { headers });
      if (res.ok) setMesas((await safeJson(res)) || []);
    } catch {
      /* silencioso */
    }
  }, [token, venueID, headers]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const crear = useCallback(async () => {
    const n = Math.max(1, Math.min(50, Number(cuantas) || 0));
    // Se numera desde el número MÁS ALTO que ya existe, no desde cuántas hay:
    // con «Mesa 1..10» y la 3 dada de baja quedan 9 activas, así que contar
    // longitudes proponía «Mesa 10» — un label vivo — y el backend rechaza hoy
    // la tanda entera. Los nombres que no son «Mesa N» (una «Terraza») no
    // entran en la cuenta, que es lo que hace que sirva de algo.
    const ultimo = mesas.reduce((max, m) => {
      const num = /^Mesa\s+(\d+)$/i.exec((m.label || "").trim());
      return num ? Math.max(max, Number(num[1])) : max;
    }, 0);
    const start = Math.max(ultimo, mesas.length) + 1;
    const labels = Array.from({ length: n }, (_, i) => `Mesa ${start + i}`);
    setBusy(true);
    setMsg("");
    try {
      const res = await apiFetch(`/v1/service-tables`, {
        method: "POST",
        headers,
        body: JSON.stringify({ venue_id: venueID, labels, zone: zona.trim() || null }),
      });
      if (!res.ok) {
        // El 409 dice CUÁLES chocan; tirarlo y decir «no se pudieron crear»
        // dejaba al encargado sin saber qué cambiar.
        const detalle = (await safeJson(res).catch(() => null))?.detail;
        throw new Error(typeof detalle === "string" && detalle ? detalle : "No se pudieron crear las mesas");
      }
      await cargar();
      setMsg(`${n} mesa${n === 1 ? "" : "s"} creada${n === 1 ? "" : "s"} ✓`);
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  }, [cuantas, zona, mesas, venueID, headers, cargar]);

  const guardarEdicion = useCallback(async () => {
    if (!editando) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await apiFetch(`/v1/service-tables/${editando.id}?venue_id=${venueID}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          label: editando.label,
          display_name: editando.display_name.trim() || null,
          zone: editando.zone.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(typeof data?.detail === "string" ? data.detail : "No se pudo guardar la mesa");
      }
      setEditando(null);
      await cargar();
      setMsg("Mesa guardada ✓ (su QR sigue siendo el mismo)");
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  }, [editando, venueID, headers, cargar]);

  const borrar = useCallback(
    async (mesa) => {
      const ok = await confirmar({
        title: `Dar de baja ${mesa.label}`,
        description: "Su QR dejará de funcionar.",
        confirmLabel: "Dar de baja",
        tone: "danger",
      });
      if (!ok) return;
      await apiFetch(`/v1/service-tables/${mesa.id}?venue_id=${venueID}`, { method: "DELETE", headers }).catch(() => {});
      cargar();
    },
    [venueID, headers, cargar, confirmar]
  );

  // Deep-link del QR: la PWA del socio resolverá /mesa/<token>. Origen actual del FE.
  const qrUrl = (mesa) => `${window.location.origin}/mesa/${mesa.qr_token}`;

  const imprimir = useCallback(() => {
    window.print();
  }, []);

  return (
    <section className="rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h2 className="text-lg font-semibold text-[var(--foreground)]">Mesas del club</h2>
          <p className="text-xs text-[var(--muted-foreground)]">
            Cada mesa lleva su QR: el socio lo escanea y pide desde su móvil. Sin stock — solo dónde se sirve.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            max={50}
            value={cuantas}
            onChange={(e) => setCuantas(e.target.value)}
            className="h-9 w-20 rounded-md border border-[var(--border)] px-2 text-sm"
            title="Cuántas mesas crear"
          />
          <input
            value={zona}
            onChange={(e) => setZona(e.target.value)}
            placeholder="Zona (opcional)"
            className="h-9 w-36 rounded-md border border-[var(--border)] px-2 text-sm"
          />
          <button
            type="button"
            disabled={busy}
            onClick={crear}
            className="h-9 rounded-md bg-[var(--foreground)] px-3 text-sm font-semibold text-white hover:opacity-[0.88] disabled:opacity-40"
          >
            Crear mesas
          </button>
          {mesas.length > 0 && (
            <button
              type="button"
              onClick={imprimir}
              className="h-9 rounded-md bg-lime-500 px-3 text-sm font-semibold text-white hover:bg-lime-600"
            >
              🖨️ Imprimir QRs
            </button>
          )}
        </div>
      </div>

      {msg && <div className="mb-2 text-xs text-[var(--muted-foreground)] print:hidden">{msg}</div>}

      {mesas.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--muted-foreground)] print:hidden">
          Aún no hay mesas. Crea la tanda y imprime sus QRs.
        </div>
      ) : (
        /* En pantalla: tarjetas compactas. Al imprimir: una cuadrícula limpia de QRs. */
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 print:grid-cols-2 print:gap-8">
          {mesas.map((m) => (
            <div
              key={m.id}
              className="flex flex-col items-center rounded-xl border border-[var(--border)] p-3 text-center print:break-inside-avoid print:border-2 print:p-8"
            >
              <div className="bg-white p-1 print:p-2">
                <QRCode value={qrUrl(m)} size={96} className="print:h-40 print:w-40" />
              </div>
              {/* Nombre propio arriba y «Mesa 3» debajo (plan CRUO §G): la etiqueta
                  impresa lleva LOS DOS, y así el papel y la pantalla no discrepan. */}
              {m.display_name ? (
                <>
                  <div className="mt-2 text-sm font-semibold text-[var(--foreground)] print:text-2xl">{m.display_name}</div>
                  <div className="text-[11px] text-[var(--muted-foreground)] print:text-lg">{m.label}</div>
                </>
              ) : (
                <div className="mt-2 text-sm font-semibold text-[var(--foreground)] print:text-2xl">{m.label}</div>
              )}
              {m.zone && <div className="text-[11px] text-[var(--muted-foreground)] print:text-base">{m.zone}</div>}
              {editando?.id === m.id ? (
                <div className="mt-2 flex w-full flex-col gap-1.5 print:hidden">
                  <input
                    value={editando.display_name}
                    onChange={(e) => setEditando((p) => ({ ...p, display_name: e.target.value }))}
                    placeholder="Nombre propio (opcional)"
                    aria-label={`Nombre propio de ${m.label}`}
                    className="h-8 w-full rounded-md border border-[var(--border)] px-2 text-xs"
                  />
                  <input
                    value={editando.label}
                    onChange={(e) => setEditando((p) => ({ ...p, label: e.target.value }))}
                    aria-label={`Referencia de ${m.label}`}
                    title="La referencia impresa junto al QR («Mesa 3»). Si la cambias, conviene reimprimir la pegatina."
                    className="h-8 w-full rounded-md border border-[var(--border)] px-2 text-xs"
                  />
                  <input
                    value={editando.zone}
                    onChange={(e) => setEditando((p) => ({ ...p, zone: e.target.value }))}
                    placeholder="Zona (opcional)"
                    aria-label={`Zona de ${m.label}`}
                    className="h-8 w-full rounded-md border border-[var(--border)] px-2 text-xs"
                  />
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={guardarEdicion}
                      className="flex-1 rounded-md bg-[var(--foreground)] py-1 text-xs font-semibold text-white hover:opacity-[0.88] disabled:opacity-40"
                    >
                      Guardar
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditando(null)}
                      className="flex-1 rounded-md border border-[var(--border)] py-1 text-xs"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-1.5 flex gap-3 print:hidden">
                  <button
                    type="button"
                    onClick={() =>
                      setEditando({
                        id: m.id,
                        label: m.label || "",
                        display_name: m.display_name || "",
                        zone: m.zone || "",
                      })
                    }
                    className="text-[11px] text-[var(--foreground)] hover:underline"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => borrar(m)}
                    className="text-[11px] text-[var(--destructive)] hover:underline"
                  >
                    Dar de baja
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {dialogo}
    </section>
  );
}
